import { useState, useEffect } from 'react'
import {
  Package,
  Eye,
  Copy,
  Star,
  Building2,
  FileText,
  BarChart3,
  Mail,
  Briefcase,
  Scale,
  TrendingUp,
  Shield,
  Heart,
  FlaskConical,
  Users,
  Megaphone,
  FileSearch,
  ClipboardCheck,
  Calculator,
  Stethoscope,
  Pill,
  BookOpen,
  Target,
  PenTool,
  ShieldCheck,
  FileCheck,
  Handshake,
  Receipt
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { Link } from 'react-router-dom'

interface PresetsTabProps {
  searchQuery: string
}

interface PresetAgent {
  id: string
  name: string
  description: string
  icon: React.ElementType
  category: string
  capabilities: string[]
  usageCount: number
  rating: number
  primaryModel: string
  isPopular: boolean
}

// Categories aligned with sample companies
const presetCategories = [
  { id: 'legal', label: 'Legal', icon: Scale },
  { id: 'finance', label: 'Finance', icon: TrendingUp },
  { id: 'insurance', label: 'Insurance', icon: Shield },
  { id: 'healthcare', label: 'Healthcare', icon: Heart },
  { id: 'pharma', label: 'Pharmaceutical', icon: FlaskConical },
  { id: 'consulting', label: 'Consulting', icon: Briefcase },
  { id: 'general', label: 'General Business', icon: Building2 },
]

// Presets aligned with sample companies:
// - Morrison & Sterling LLP (Legal)
// - Meridian Capital Advisors (Finance)
// - Guardian Insurance Group (Insurance)
// - Regional Medical Center Network (Healthcare)
// - BioVantage Therapeutics (Pharmaceutical)
// - Strategos Consulting (Consulting)
// - ACME Corporation (General)
const mockPresets: PresetAgent[] = [
  // === LEGAL (Morrison & Sterling LLP) ===
  {
    id: 'legal-1',
    name: 'Contract Analyzer',
    description: 'Analyzes contracts for key terms, risks, and obligations. Identifies problematic clauses and suggests improvements.',
    icon: FileSearch,
    category: 'legal',
    capabilities: ['Risk Identification', 'Clause Extraction', 'Obligation Tracking', 'Comparison Analysis'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'claude-3-sonnet',
    isPopular: true,
  },
  {
    id: 'legal-2',
    name: 'Legal Brief Writer',
    description: 'Drafts legal briefs and memoranda with proper citations and legal formatting for Morrison & Sterling practices.',
    icon: Scale,
    category: 'legal',
    capabilities: ['Legal Citations', 'Case Law Research', 'Argument Structure', 'Jurisdiction Awareness'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'claude-3-sonnet',
    isPopular: true,
  },
  {
    id: 'legal-3',
    name: 'Due Diligence Assistant',
    description: 'Supports M&A due diligence by reviewing documents, flagging issues, and creating summary reports.',
    icon: ClipboardCheck,
    category: 'legal',
    capabilities: ['Document Review', 'Issue Flagging', 'Summary Reports', 'Checklist Management'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o',
    isPopular: false,
  },

  // === FINANCE (Meridian Capital Advisors) ===
  {
    id: 'finance-1',
    name: 'Investment Report Generator',
    description: 'Creates quarterly investor reports and portfolio performance summaries for Meridian Capital clients.',
    icon: BarChart3,
    category: 'finance',
    capabilities: ['Performance Metrics', 'Market Commentary', 'Risk Analysis', 'Benchmark Comparison'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o',
    isPopular: true,
  },
  {
    id: 'finance-2',
    name: 'Financial Analysis Assistant',
    description: 'Analyzes financial statements, ratios, and market data to support investment decisions.',
    icon: Calculator,
    category: 'finance',
    capabilities: ['Ratio Analysis', 'Trend Detection', 'Peer Comparison', 'Valuation Models'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'claude-3-sonnet',
    isPopular: true,
  },
  {
    id: 'finance-3',
    name: 'Client Portfolio Reviewer',
    description: 'Reviews client portfolios and generates personalized recommendations based on risk tolerance and goals.',
    icon: TrendingUp,
    category: 'finance',
    capabilities: ['Portfolio Analysis', 'Rebalancing Suggestions', 'Risk Assessment', 'Goal Tracking'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o',
    isPopular: false,
  },

  // === INSURANCE (Guardian Insurance Group) ===
  {
    id: 'insurance-1',
    name: 'Policy Document Generator',
    description: 'Generates insurance policy documents, declarations, and certificates for Guardian Insurance products.',
    icon: FileCheck,
    category: 'insurance',
    capabilities: ['Policy Drafting', 'Coverage Summaries', 'Endorsements', 'Declaration Pages'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o',
    isPopular: true,
  },
  {
    id: 'insurance-2',
    name: 'Claims Processing Assistant',
    description: 'Assists with claims intake, documentation review, and initial assessment for faster processing.',
    icon: Receipt,
    category: 'insurance',
    capabilities: ['Claims Intake', 'Document Verification', 'Coverage Matching', 'Status Updates'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'claude-3-sonnet',
    isPopular: true,
  },
  {
    id: 'insurance-3',
    name: 'Underwriting Report Writer',
    description: 'Creates detailed underwriting reports with risk assessments and pricing recommendations.',
    icon: ShieldCheck,
    category: 'insurance',
    capabilities: ['Risk Scoring', 'Premium Calculation', 'Loss History Analysis', 'Recommendations'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o',
    isPopular: false,
  },

  // === HEALTHCARE (Regional Medical Center Network) ===
  {
    id: 'healthcare-1',
    name: 'Clinical Documentation Assistant',
    description: 'Helps create discharge summaries, care plans, and clinical notes while ensuring HIPAA compliance.',
    icon: Stethoscope,
    category: 'healthcare',
    capabilities: ['Discharge Summaries', 'Care Plans', 'Clinical Notes', 'ICD-10 Coding'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'claude-3-sonnet',
    isPopular: true,
  },
  {
    id: 'healthcare-2',
    name: 'Patient Education Writer',
    description: 'Creates patient-friendly educational materials about conditions, treatments, and procedures.',
    icon: BookOpen,
    category: 'healthcare',
    capabilities: ['Plain Language', 'Multilingual', 'Visual Aids', 'Condition Specific'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o',
    isPopular: false,
  },
  {
    id: 'healthcare-3',
    name: 'HIPAA Compliance Checker',
    description: 'Reviews documents for PHI and ensures compliance with HIPAA privacy and security requirements.',
    icon: Shield,
    category: 'healthcare',
    capabilities: ['PHI Detection', 'De-identification', 'Compliance Audit', 'Risk Assessment'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'claude-3-sonnet',
    isPopular: true,
  },

  // === PHARMACEUTICAL (BioVantage Therapeutics) ===
  {
    id: 'pharma-1',
    name: 'Clinical Protocol Writer',
    description: 'Drafts clinical study protocols following ICH-GCP guidelines for BioVantage trials.',
    icon: FlaskConical,
    category: 'pharma',
    capabilities: ['Protocol Design', 'ICH-GCP Compliance', 'Statistical Methods', 'Safety Monitoring'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'claude-3-sonnet',
    isPopular: true,
  },
  {
    id: 'pharma-2',
    name: 'Regulatory Submission Assistant',
    description: 'Helps prepare FDA/EMA submissions, including IND applications and NDA/BLA documents.',
    icon: FileText,
    category: 'pharma',
    capabilities: ['IND Preparation', 'CTD Formatting', 'eCTD Assembly', 'Regulatory Strategy'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o',
    isPopular: true,
  },
  {
    id: 'pharma-3',
    name: 'Medical Writing Assistant',
    description: 'Creates CSRs, investigator brochures, and scientific publications for research teams.',
    icon: Pill,
    category: 'pharma',
    capabilities: ['CSR Writing', 'Literature Review', 'Data Visualization', 'Peer Review Prep'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'claude-3-sonnet',
    isPopular: false,
  },

  // === CONSULTING (Strategos Consulting) ===
  {
    id: 'consulting-1',
    name: 'Consulting Proposal Writer',
    description: 'Creates compelling consulting proposals and RFP responses for Strategos client engagements.',
    icon: Handshake,
    category: 'consulting',
    capabilities: ['Proposal Structure', 'Pricing Strategy', 'Case Studies', 'Win Themes'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o',
    isPopular: true,
  },
  {
    id: 'consulting-2',
    name: 'Strategy Report Generator',
    description: 'Generates strategic assessment reports with market analysis and recommendations.',
    icon: Target,
    category: 'consulting',
    capabilities: ['Market Analysis', 'Competitive Intelligence', 'SWOT Analysis', 'Recommendations'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'claude-3-sonnet',
    isPopular: true,
  },
  {
    id: 'consulting-3',
    name: 'Executive Summary Creator',
    description: 'Distills complex analyses into clear, actionable executive summaries for C-suite audiences.',
    icon: PenTool,
    category: 'consulting',
    capabilities: ['Key Insights', 'Visual Summaries', 'Action Items', 'Decision Framework'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o',
    isPopular: false,
  },

  // === GENERAL BUSINESS (ACME Corporation) ===
  {
    id: 'general-1',
    name: 'Marketing Content Creator',
    description: 'Creates marketing copy, blog posts, and campaign materials aligned with brand guidelines.',
    icon: Megaphone,
    category: 'general',
    capabilities: ['Brand Voice', 'SEO Optimization', 'Multi-Channel', 'A/B Variants'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o',
    isPopular: true,
  },
  {
    id: 'general-2',
    name: 'Sales Proposal Generator',
    description: 'Builds customized sales proposals with pricing, timelines, and value propositions.',
    icon: Briefcase,
    category: 'general',
    capabilities: ['Custom Pricing', 'ROI Calculator', 'Competitor Comparison', 'Follow-up Sequences'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o',
    isPopular: true,
  },
  {
    id: 'general-3',
    name: 'HR Document Assistant',
    description: 'Creates HR documents including policies, job descriptions, and employee communications.',
    icon: Users,
    category: 'general',
    capabilities: ['Policy Drafting', 'Job Descriptions', 'Onboarding Materials', 'Compliance'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'claude-3-sonnet',
    isPopular: false,
  },
  {
    id: 'general-4',
    name: 'Email Communication Writer',
    description: 'Drafts professional emails for internal and external communications across departments.',
    icon: Mail,
    category: 'general',
    capabilities: ['Professional Tone', 'Templates', 'Follow-ups', 'Meeting Requests'],
    usageCount: 0,
    rating: 0,
    primaryModel: 'gpt-4o-mini',
    isPopular: false,
  },
]

export function PresetsTab({ searchQuery }: PresetsTabProps) {
  const { toast } = useToast()
  const [presets, setPresets] = useState<PresetAgent[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPreset, setSelectedPreset] = useState<PresetAgent | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  useEffect(() => {
    loadPresets()
  }, [])

  const loadPresets = async () => {
    setLoading(true)
    try {
      // In production, call API
      setPresets(mockPresets)
    } finally {
      setLoading(false)
    }
  }

  const filteredPresets = presets.filter((preset) => {
    const matchesSearch =
      preset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      preset.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = !selectedCategory || preset.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const handleApplyPreset = (preset: PresetAgent) => {
    toast({
      title: 'Preset Applied',
      description: `Creating new agent based on "${preset.name}"`,
    })
    // Navigate to builder with preset data
    // In production: navigate(`/ai-studio/builder?preset=${preset.id}`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    )
  }

  return (
    <>
      {/* Category Filters */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        <Button
          variant={selectedCategory === null ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory(null)}
          className="rounded-full whitespace-nowrap"
        >
          All Presets
        </Button>
        {presetCategories.map((category) => {
          const Icon = category.icon
          return (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category.id)}
              className="rounded-full whitespace-nowrap"
            >
              <Icon className="h-3 w-3 mr-1.5" />
              {category.label}
            </Button>
          )
        })}
      </div>

      {filteredPresets.length === 0 ? (
        <div className="text-center py-12">
          <div className="p-4 rounded-full bg-amber-100 dark:bg-amber-900/30 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Package className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No presets found</h3>
          <p className="text-gray-500 dark:text-gray-400">
            Try adjusting your search or filter criteria
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredPresets.map((preset) => {
            const Icon = preset.icon

            return (
              <div
                key={preset.id}
                className="group relative rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 hover:shadow-lg hover:border-amber-300 dark:hover:border-amber-600 transition-all"
              >
                {preset.isPopular && (
                  <div className="absolute -top-2 -right-2">
                    <Badge className="bg-amber-500 text-white rounded-full px-2 py-0.5 text-xs">
                      <Star className="h-3 w-3 mr-1 fill-current" />
                      Popular
                    </Badge>
                  </div>
                )}

                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{preset.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {presetCategories.find((c) => c.id === preset.category)?.label}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                  {preset.description}
                </p>

                {/* Capabilities */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {preset.capabilities.slice(0, 3).map((cap) => (
                    <Badge
                      key={cap}
                      className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-full px-2 py-0.5 text-xs"
                    >
                      {cap}
                    </Badge>
                  ))}
                  {preset.capabilities.length > 3 && (
                    <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-full px-2 py-0.5 text-xs">
                      +{preset.capabilities.length - 3}
                    </Badge>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                    {preset.rating}
                  </div>
                  <span>{preset.usageCount.toLocaleString()} uses</span>
                  <span className="text-xs">{preset.primaryModel}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedPreset(preset)
                      setPreviewOpen(true)
                    }}
                    className="flex-1 rounded-xl"
                  >
                    <Eye className="mr-2 h-3 w-3" />
                    Preview
                  </Button>
                  <Link to={`/ai-studio/builder?preset=${preset.id}`} className="flex-1">
                    <Button
                      size="sm"
                      onClick={() => handleApplyPreset(preset)}
                      className="w-full rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <Copy className="mr-2 h-3 w-3" />
                      Apply
                    </Button>
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          {selectedPreset && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600">
                    <selectedPreset.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <DialogTitle className="text-xl">{selectedPreset.name}</DialogTitle>
                    <DialogDescription className="text-base mt-1">
                      {selectedPreset.description}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Capabilities</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedPreset.capabilities.map((cap) => (
                      <Badge
                        key={cap}
                        className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full"
                      >
                        {cap}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Primary Model</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedPreset.primaryModel}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Usage</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {selectedPreset.usageCount.toLocaleString()} times
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                  <span className="font-medium text-gray-900 dark:text-white">{selectedPreset.rating}</span>
                  <span className="text-gray-500 dark:text-gray-400">average rating</span>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                  Cancel
                </Button>
                <Link to={`/ai-studio/builder?preset=${selectedPreset.id}`}>
                  <Button
                    onClick={() => {
                      handleApplyPreset(selectedPreset)
                      setPreviewOpen(false)
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Apply Preset
                  </Button>
                </Link>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
